import React from 'react';
import {
  Container, Typography, Box, Card, CardContent, Tabs, Tab, Grid, TextField,
  Button, FormControl, InputLabel, Select, MenuItem, FormHelperText,
  Switch, FormControlLabel, Avatar, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, Alert, CircularProgress, Snackbar,
  Dialog, DialogTitle, DialogContent, DialogActions, Paper, Chip, IconButton,
  Link, CardActions, Divider, Collapse, Autocomplete
} from '@mui/material';
import {
  Business as BusinessIcon, Add as AddIcon, Edit as EditIcon,
  Delete as DeleteIcon, Save as SaveIcon, LocalOffer as LocalOfferIcon,
  ShoppingCart as ShoppingCartIcon, Settings as SettingsIcon,
  FileDownload as ExportIcon, Link as LinkIcon, Phone as PhoneIcon,
  Tablet as TabletIcon, Computer as ComputerIcon, Article as ArticleIcon,
  Schedule as ScheduleIcon, Close as CloseIcon, Category as CategoryIcon,
  ViewModule as TemplateIcon, TrendingUp as UsageIcon,
  Visibility as PreviewIcon, Create as CreateIcon,
  KeyboardArrowDown as KeyboardArrowDownIcon,
  KeyboardArrowRight as KeyboardArrowRightIcon
} from '@mui/icons-material';
import { styled } from '@mui/material/styles';
import dayjs from 'dayjs';
import { Link as RouterLink } from 'react-router-dom';
import OfferPageBuilderSimple from '../../../components/OfferPageBuilderSimple';
import { useBrandsContext } from '../../BrandsContext';

const OfferSetupTab = () => {
  const {
    user, navigate,
    activeTab, setActiveTab,
    brandForm, setBrandForm,
    existingBrands, setExistingBrands,
    loading, setLoading,
    error, setError,
    success, setSuccess,
    editingBrand, setEditingBrand,
    editDialogOpen, setEditDialogOpen,
    deleteDialogOpen, setDeleteDialogOpen,
    brandToDelete, setBrandToDelete,
    offerLoading, setOfferLoading,
    offerError, setOfferError,
    categories, setCategories,
    selectedCategory, setSelectedCategory,
    offerForm, setOfferForm,
    editingOffer, setEditingOffer,
    editOfferDialogOpen, setEditOfferDialogOpen,
    deleteOfferDialogOpen, setDeleteOfferDialogOpen,
    offerToDelete, setOfferToDelete,
    availableOffers, setAvailableOffers,
    availableOffersLoading, setAvailableOffersLoading,
    expandedBrands, setExpandedBrands,
    brandOffers, setBrandOffers,
    sharedElements, setSharedElements,
    sharedSelectedElement, setSharedSelectedElement,
    sharedSelectedElementContext, setSharedSelectedElementContext,
    sharedSelectedCategory, setSharedSelectedCategory,
    sharedSelectedSubtype, setSharedSelectedSubtype,
    sharedSelectedBrand, setSharedSelectedBrand,
    sharedSelectedOffer, setSharedSelectedOffer,
    sharedPages, setSharedPages,
    sharedSelectedTemplateId, setSharedSelectedTemplateId,
    fetchBrands, fetchCategories, fetchAvailableOffers,
    handleToggleBrandOffers, handleTabChange, handleOfferFormChange,
    handleCreateOffer, handleEditOffer, handleDeleteOfferClick,
    handleDeleteOfferConfirm, handleDeleteOfferCancel,
    handleEditOfferCancel, handleEditBrand, handleUpdateBrand,
    handleDeleteClick, handleDeleteConfirm, handleDeleteCancel,
    handleCreateBrand,
    isValidUrl,
  } = useBrandsContext();

  return (
    <Box>
      <Typography variant="h6" gutterBottom>
        Create New Offer
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Fill in the details below to create a new offer
      </Typography>
    
      {offerError && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {offerError.split('\n').map((error, index) => (
            <div key={index}>{error}</div>
          ))}
        </Alert>
      )}
    
      {success && (
        <Alert severity="success" sx={{ mb: 2 }}>
          {success}
        </Alert>
      )}
    
      <Card>
        <CardContent>
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <Autocomplete
                options={existingBrands}
                getOptionLabel={(option) => option.name || ''}
                value={existingBrands.find(brand => brand._id === offerForm.brand) || null}
                onChange={(event, newValue) => {
                  setOfferForm(prev => ({
                    ...prev,
                    brand: newValue ? newValue._id : ''
                  }));
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Brand"
                    placeholder="Search brands..."
                    helperText="Select the brand this offer is associated with"
                  />
                )}
                renderOption={(props, option) => (
                  <Box component="li" {...props}>
                    <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                      <Typography variant="body1">{option.name}</Typography>
                      {option.company && (
                        <Typography variant="caption" color="text.secondary">
                          {option.company}
                        </Typography>
                      )}
                    </Box>
                  </Box>
                )}
                isOptionEqualToValue={(option, value) => option._id === value._id}
                clearOnBlur={false}
                blurOnSelect={true}
              />
            </Grid>
    
            <Grid item xs={12} md={6}>
              <TextField
                label="Company"
                value={existingBrands.find(brand => brand._id === offerForm.brand)?.company || ''}
                fullWidth
                disabled
                placeholder="Company name will appear when brand is selected"
                helperText="Company name from selected brand"
              />
            </Grid>
    
            <Grid item xs={12} md={6}>
              <TextField
                label="Offer Name"
                value={offerForm.title}
                onChange={handleOfferFormChange('title')}
                fullWidth
                required
                placeholder="Enter offer name"
              />
            </Grid>
    
            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel>Type of Action</InputLabel>
                <Select
                  value={offerForm.typeOfAction}
                  onChange={handleOfferFormChange('typeOfAction')}
                >
                  <MenuItem value="download">download</MenuItem>
                  <MenuItem value="lead">lead</MenuItem>
                  <MenuItem value="sale">sale</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel>Pays on</InputLabel>
                <Select
                  value={offerForm.paysOn}
                  onChange={handleOfferFormChange('paysOn')}
                >
                  <MenuItem value="Click">Click</MenuItem>
                  <MenuItem value="Conversion">Conversion</MenuItem>
                  <MenuItem value="Revenue Share">Revenue Share</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            {/* Same visual field either way — what it holds and how it's
                validated switches based on "Pays on". Selecting Revenue
                Share here is what actually flips Offer.payoutMethod to
                'revenue_share' server-side; see handleCreateOffer. */}
            <Grid item xs={12} md={6}>
              {offerForm.paysOn === 'Revenue Share' ? (
                <TextField
                  label="Revenue Share %"
                  type="number"
                  value={offerForm.revenueSharePercent}
                  onChange={handleOfferFormChange('revenueSharePercent')}
                  fullWidth
                  required
                  placeholder="e.g., 10"
                  inputProps={{ min: 0, max: 100, step: 0.1 }}
                  helperText="% of the total sale amount the brand reports to us that we're paid — e.g. 10 for 10%"
                  error={offerError && offerError.includes('Revenue Share')}
                />
              ) : (
                <TextField
                  label="Payout"
                  value={offerForm.payout}
                  onChange={handleOfferFormChange('payout')}
                  fullWidth
                  required
                  placeholder="e.g., 50 or $50"
                  helperText="Numeric commission per conversion — used server-side when the advertiser's postback doesn't include one (the standard postback URL for this doesn't)"
                  error={offerError && offerError.includes('Payout is required')}
                />
              )}
            </Grid>

            <Grid item xs={12}>
              <TextField
                label="Offer URL *"
                value={offerForm.purchaseUrl}
                onChange={handleOfferFormChange('purchaseUrl')}
                fullWidth
                required
                placeholder="https://example.com/offer"
                error={
                  (offerError && offerError.includes('Purchase URL')) ||
                  (offerForm.purchaseUrl && offerForm.purchaseUrl.trim() && !isValidUrl(offerForm.purchaseUrl.trim()))
                }
                helperText={
                  offerError && offerError.includes('Purchase URL')
                    ? 'Purchase URL is required'
                    : offerForm.purchaseUrl && offerForm.purchaseUrl.trim() && !isValidUrl(offerForm.purchaseUrl.trim())
                      ? 'Please enter a valid URL (e.g., https://example.com/offer)'
                      : ''
                }
              />
            </Grid>
    
            <Grid item xs={12}>
              <TextField
                label="Description *"
                value={offerForm.description}
                onChange={handleOfferFormChange('description')}
                fullWidth
                multiline
                rows={3}
                placeholder="Describe the offer details"
                required
                error={offerError && offerError.includes('description')}
                helperText={offerError && offerError.includes('description') ? 'Description is required' : ''}
              />
            </Grid>
    
            <Grid item xs={12} md={6}>
              <FormControl fullWidth required error={offerError && offerError.includes('Category')}>
                <InputLabel>Category *</InputLabel>
                <Select
                  value={offerForm.category}
                  onChange={handleOfferFormChange('category')}
                >
                  {categories.map((category) => (
                    <MenuItem key={category._id} value={category._id}>
                      {category.name}
                    </MenuItem>
                  ))}
                </Select>
                {offerError && offerError.includes('Category') && (
                  <FormHelperText error>Category is required</FormHelperText>
                )}
              </FormControl>
            </Grid>
    
            <Grid item xs={12} md={6}>
              <FormControl fullWidth required disabled={!selectedCategory} error={offerError && offerError.includes('Subcategory')}>
                <InputLabel>Sub Category *</InputLabel>
                <Select
                  value={offerForm.subcategoryId}
                  onChange={handleOfferFormChange('subcategoryId')}
                >
                  {selectedCategory?.subcategories?.map((subcategory) => (
                    <MenuItem key={subcategory._id} value={subcategory._id}>
                      {subcategory.name}
                    </MenuItem>
                  ))}
                </Select>
                {offerError && offerError.includes('Subcategory') && (
                  <FormHelperText error>Subcategory is required</FormHelperText>
                )}
              </FormControl>
            </Grid>
    
            <Grid item xs={12}>
              <FormControl fullWidth>
                <InputLabel>Problem Solved</InputLabel>
                <Select
                  value={offerForm.problemSolved}
                  onChange={handleOfferFormChange('problemSolved')}
                  label="Problem Solved"
                >
                  <MenuItem value="">
                    <em>Select a problem this offer solves</em>
                  </MenuItem>
                  <MenuItem value="gut pain">Gut Pain</MenuItem>
                  <MenuItem value="headache">Headache</MenuItem>
                  <MenuItem value="stress reduction">Stress Reduction</MenuItem>
                  <MenuItem value="can't sleep">Can't Sleep</MenuItem>
                  <MenuItem value="better sleep">Better Sleep</MenuItem>
                  <MenuItem value="focus">Focus</MenuItem>
                  <MenuItem value="back pain">Back Pain</MenuItem>
                </Select>
                <FormHelperText>Select the main problem this offer helps solve</FormHelperText>
              </FormControl>
            </Grid>
    
            <Grid item xs={12}>
              <FormControl fullWidth>
                <InputLabel>Countries Accepted</InputLabel>
                <Select
                  multiple
                  value={offerForm.countriesAccepted}
                  onChange={handleOfferFormChange('countriesAccepted')}
                  renderValue={(selected) => (
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                      {selected.map((value) => {
                        const countryNames = {
                          'Global': 'Global (All Countries)',
                          'US': 'United States',
                          'CA': 'Canada',
                          'UK': 'United Kingdom',
                          'AU': 'Australia',
                          'DE': 'Germany',
                          'FR': 'France',
                        };
                        return (
                          <Chip
                            key={value}
                            label={countryNames[value] || value}
                            onDelete={(event) => {
                              event.stopPropagation();
                              const newCountries = offerForm.countriesAccepted.filter(country => country !== value);
                              setOfferForm(prev => ({ ...prev, countriesAccepted: newCountries }));
                            }}
                            size="small"
                          />
                        );
                      })}
                    </Box>
                  )}
                >
                  <MenuItem value="Global">Global (All Countries)</MenuItem>
                  <MenuItem value="US">United States</MenuItem>
                  <MenuItem value="CA">Canada</MenuItem>
                  <MenuItem value="UK">United Kingdom</MenuItem>
                  <MenuItem value="AU">Australia</MenuItem>
                  <MenuItem value="DE">Germany</MenuItem>
                  <MenuItem value="FR">France</MenuItem>
                </Select>
              </FormControl>
            </Grid>
    
            <Grid item xs={12}>
              <FormControl fullWidth>
                <InputLabel id="traffic-sources-label">Traffic Sources Allowed</InputLabel>
                <Select
                  labelId="traffic-sources-label"
                  label="Traffic Sources Allowed"
                  value={offerForm.trafficSources ? (Array.isArray(offerForm.trafficSources) ? offerForm.trafficSources : offerForm.trafficSources.split(',').map(s => s.trim()).filter(s => s)) : []}
                  onChange={(event) => setOfferForm(prev => ({ ...prev, trafficSources: event.target.value }))}
                  multiple
                  renderValue={(selected) => (
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                      {selected.map((value) => (
                        <Chip key={value} label={value} size="small" />
                      ))}
                    </Box>
                  )}
                >
                  <MenuItem value="Paid Search">Paid Search</MenuItem>
                  <MenuItem value="Google Search">Google Search</MenuItem>
                  <MenuItem value="TikTok Unpaid">TikTok Unpaid</MenuItem>
                  <MenuItem value="Instagram Unpaid">Instagram Unpaid</MenuItem>
                  <MenuItem value="Facebook Unpaid">Facebook Unpaid</MenuItem>
                  <MenuItem value="Meta Ads">Meta Ads</MenuItem>
                  <MenuItem value="TikTok Ads">TikTok Ads</MenuItem>
                  <MenuItem value="Mobile Ads">Mobile Ads</MenuItem>
                  <MenuItem value="Pops">Pops</MenuItem>
                  <MenuItem value="Native Ads">Native Ads</MenuItem>
                  <MenuItem value="Display Ads">Display Ads</MenuItem>
                </Select>
                <FormHelperText>Select which traffic sources are allowed for this offer</FormHelperText>
              </FormControl>
            </Grid>
    
            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel>Tracking Type</InputLabel>
                <Select
                  value={offerForm.trackingType}
                  onChange={handleOfferFormChange('trackingType')}
                >
                  <MenuItem value="affiliate network">Affiliate Network</MenuItem>
                  <MenuItem value="server to server">Server to Server</MenuItem>
                  <MenuItem value="pixel">Pixel</MenuItem>
                </Select>
                <FormHelperText>Select the tracking method for this offer</FormHelperText>
              </FormControl>
            </Grid>
    
            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Switch
                    checked={offerForm.published}
                    onChange={(e) => setOfferForm(prev => ({ ...prev, published: e.target.checked }))}
                  />
                }
                label="Published"
              />
            </Grid>
    
            <Grid item xs={12}>
              <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
                <Button
                  variant="outlined"
                  onClick={() => {
                    setOfferForm({
                      title: '',
                      payout: '',
                      typeOfAction: '',
                      paysOn: '',
                      revenueSharePercent: '',
                      purchaseUrl: '',
                      description: '',
                      category: '',
                      subcategoryId: '',
                      problemSolved: '',
                      countriesAccepted: [],
                      trafficSources: '',
                      trackingType: '',
                      brand: '',
                      expirationDate: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000),
                      published: true
                    });
                    setSelectedCategory(null);
                  }}
                >
                  Clear Form
                </Button>
                <Button
                  variant="contained"
                  onClick={handleCreateOffer}
                  disabled={offerLoading}
                  startIcon={offerLoading ? <CircularProgress size={20} /> : <SaveIcon />}
                >
                  Create Offer
                </Button>
              </Box>
            </Grid>
          </Grid>
        </CardContent>
      </Card>
    </Box>
  );
};

export default OfferSetupTab;
